import React, {useEffect, useState, useMemo, useCallback, useRef} from "react";
import {useParams, useNavigate, useLocation, useSearchParams} from "react-router-dom";
import chevronLeft from "../../assets/chevron-left.svg";
import copyIcon from "../../assets/copy.svg";
import {getExternalOrder} from "../../services/order-service.js";
import {fetchAdjustableSpiceLevels} from "../../services/menu-service.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import Toast from "../../components/Toast/toast.js";
import {useLanguage} from "../../utils/languageContext.js";
import {getTranslation, getLocalizedValue} from "../../utils/translate.js";
import {useDarkMode} from "../../utils/darkModeContext.js";
import {getOrderStatusStyles} from "../../utils/orderStatusUtils.js";
import {formatPriceWithCurrency} from "../../utils/formatUtils.js";
import {formatDateTimeWithOffset, formatDate, formatTime} from "../../utils/dateUtils.js";
import {DISH_IMAGE_PLACEHOLDER_40} from "../../utils/dishImagePlaceholder.js";
import {getExternalAdminOrderEditUrl} from "../../utils/externalOrderAdminUrls.js";
import {resolveOrderCustomer} from "../../utils/orderCustomer.js";

/**
 * Read-only order detail for integration (non-BYCHEF) orders.
 * Route: /orders/external/:orderNumber?source=…
 */
const OrderDetailsExternal = () => {
    const {orderNumber} = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const {language} = useLanguage();
    const {isDarkMode} = useDarkMode();
    const [orderInfo, setOrderInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [spiceLevels, setSpiceLevels] = useState([]);
    const isFetchingOrder = useRef(false);
    const isFetchingSpiceLevels = useRef(false);
    const toastRef = useRef(null);

    const sourceFromUrl = searchParams.get("source") ?? "";
    const sourceFromNav =
        location.state != null &&
        typeof location.state.source === "string" &&
        location.state.source.trim() !== ""
            ? location.state.source.trim()
            : "";
    const externalSource = sourceFromUrl !== "" ? sourceFromUrl : sourceFromNav;

    const getOptionStyles = useCallback((option) => getOrderStatusStyles(option, isDarkMode), [isDarkMode]);

    useEffect(() => {
        const fetchOrderData = async () => {
            if (!orderNumber || !externalSource || externalSource === "BYCHEF") {
                setLoading(false);
                navigate("/orders", {replace: true});
                toastRef.current?.addToast(getTranslation("orderContentPage.orderNotFound", language), "error");
                return;
            }

            if (isFetchingOrder.current) return;
            isFetchingOrder.current = true;

            setLoading(true);
            try {
                const order = await getExternalOrder(orderNumber, externalSource);

                if (!isFetchingSpiceLevels.current && spiceLevels.length === 0) {
                    isFetchingSpiceLevels.current = true;
                    try {
                        const spiceLevelsData = await fetchAdjustableSpiceLevels();
                        if (spiceLevelsData) {
                            setSpiceLevels(spiceLevelsData);
                        }
                    } catch (e) {
                        console.error("Error fetching spice levels:", e);
                    } finally {
                        isFetchingSpiceLevels.current = false;
                    }
                }

                if (order) {
                    setOrderInfo(order);
                } else {
                    navigate("/orders", {replace: true});
                    toastRef.current?.addToast(getTranslation("orderContentPage.orderNotFound", language), "error");
                }
            } catch (error) {
                console.error("Error fetching external order:", error);
                toastRef.current?.addToast(getTranslation("common.error", language), "error");
                navigate("/orders", {replace: true});
            } finally {
                setLoading(false);
                isFetchingOrder.current = false;
            }
        };

        fetchOrderData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderNumber, navigate, language, externalSource]);

    const {
        number,
        receiverPhoneNumber = "",
        deliveryDateTime = "",
        addressDto = {},
        note = "",
        totalPrice = "",
        totalPriceBeforeDiscount = "",
        orderDishList = [],
        createdAt = "",
        asSoonAsPossible = false,
        promoCodeUsages = null,
        promoCode = null,
        companyName = null,
        userDto = null,
        externalUser = null
    } = orderInfo || {};

    const customer = resolveOrderCustomer({
        userDto,
        externalUser,
        receiverPhoneNumber,
        companyName
    });
    const customerFullName = customer.fullName;
    const customerPhone = customer.phone;
    const displayCompanyName = customer.companyName;

    const formattedTotalPrice = useMemo(
        () => formatPriceWithCurrency(totalPriceBeforeDiscount || totalPrice || 0),
        [totalPriceBeforeDiscount, totalPrice]
    );

    const formattedTotalWithDiscount = useMemo(() => formatPriceWithCurrency(totalPrice || 0), [totalPrice]);

    const discountAmount = useMemo(() => {
        if (!totalPriceBeforeDiscount || !totalPrice) return null;
        const before = parseFloat(totalPriceBeforeDiscount);
        const after = parseFloat(totalPrice);
        if (isNaN(before) || isNaN(after) || before <= after) return null;
        return before - after;
    }, [totalPriceBeforeDiscount, totalPrice]);

    const externalAdminUrl = useMemo(() => {
        if (!orderInfo) return null;
        const adminOrderId =
            orderInfo.id ??
            orderInfo.nopCommerceOrderId ??
            orderInfo.externalOrderId ??
            orderNumber;
        return getExternalAdminOrderEditUrl(externalSource, adminOrderId);
    }, [orderInfo, externalSource, orderNumber]);

    const handleCopyOrderId = () => {
        if (!orderInfo) return;
        navigator.clipboard.writeText(orderInfo.number || String(orderNumber));
        toastRef.current?.addToast(getTranslation("common.success", language), "success");
    };

    const onBack = () => {
        if (location.state?.fromPage === "clients") {
            navigate("/clients");
            return;
        }

        if (location.state?.fromPage === "board") {
            navigate("/orders/board");
            return;
        }
        const fromTab = location.state?.fromTab || "ongoing";
        const ongoingSubTab = location.state?.ongoingSubTab;
        if (fromTab === "dishes") {
            navigate("/orders", {state: {activeTab: "ongoing", ongoingSubTab: "dishes"}});
            return;
        }
        if (fromTab === "history") {
            navigate("/orders", {state: {activeTab: "history"}});
            return;
        }
        navigate("/orders", {
            state: {
                activeTab: "ongoing",
                ongoingSubTab: ongoingSubTab === "dishes" ? "dishes" : "orders"
            }
        });
    };

    if (loading) {
        return <LoadingSpinner/>;
    }

    if (!orderInfo) {
        return <div className="p-4">{getTranslation("orderContentPage.orderNotFound", language)}</div>;
    }

    const isTakeaway = orderInfo?.deliveryMethod === "TAKEAWAY";

    const addressQuery = encodeURIComponent(
        `${addressDto?.country || ""} ${addressDto?.city || ""} ${addressDto?.street || ""} ${addressDto?.home || ""}`
    );
    const yandexMapUrl = `https://yandex.com/maps/?text=${addressQuery}`;

    return (
        <div className="max-w-7xl mx-auto bg-white dark:bg-[#161b22] p-4 border dark:border-[#30363d] rounded-lg">
            <Button variant="secondary" size="md" onClick={onBack} className="mb-6 font-bold">
                <img
                    className="w-4 h-4 mr-2 dark:invert dark:opacity-80"
                    src={chevronLeft || ""}
                    alt={getTranslation("pagination.previous", language)}
                />
                {getTranslation("orderContentPage.backToList", language)}
            </Button>

            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-3 min-w-0">
                    <div className="flex items-center min-w-0">
                        <h1 className="text-gray-900 dark:text-[#c9d1d9] text-xl font-bold shrink-0">
                            {getTranslation("orderContentPage.order", language)}
                        </h1>
                        <p className="font-bold text-sm text-gray-900 dark:text-[#c9d1d9] ml-3 truncate">
                            #{number || orderNumber}
                        </p>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCopyOrderId}
                            title={getTranslation("orderContentPage.copyOrderId", language)}
                            className="ml-2 p-1 shrink-0"
                        >
                            <img
                                className="w-4 h-4 cursor-pointer dark:invert dark:opacity-80"
                                src={copyIcon || ""}
                                alt={getTranslation("common.copy", language)}
                            />
                        </Button>
                    </div>
                    {externalAdminUrl ? (
                        <Button
                            variant="primary"
                            size="md"
                            className="font-bold w-full sm:w-auto"
                            onClick={() => window.open(externalAdminUrl, "_blank", "noopener,noreferrer")}
                        >
                            View order in external source
                        </Button>
                    ) : null}
                </div>
                <div className="md:w-auto flex flex-wrap items-center justify-end gap-2 shrink-0">
                    <div
                        className="px-4 py-2 rounded-md font-bold text-sm whitespace-nowrap w-[221px] text-center"
                        style={{
                            backgroundColor: getOptionStyles({value: orderInfo?.status}).backgroundColor,
                            color: getOptionStyles({value: orderInfo?.status}).color
                        }}
                    >
                        {getTranslation(`orderStatuses.${orderInfo?.status?.toLowerCase()}`, language)}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 pr-0 lg:pr-6">
                    <div className="mb-4">
                        <p className="text-gray-900 dark:text-[#c9d1d9] font-bold text-base">
                            {getTranslation("orderContentPage.transactionDate", language)}:{" "}
                            {formatDateTimeWithOffset(createdAt || deliveryDateTime)}
                        </p>
                    </div>

                    <div className="bg-white dark:bg-[#161b22] rounded mb-5 border dark:border-[#30363d] p-4">
                        <h3 className="font-bold mb-4 text-lg text-gray-900 dark:text-[#c9d1d9]">
                            {getTranslation("orderContentPage.orderedDishes", language)}
                        </h3>
                        {orderDishList?.map((dish, index) => (
                            <div key={index} className="mb-4 pb-4 border-b dark:border-[#30363d] last:border-b-0">
                                <div className="flex items-center mb-2">
                                    <img
                                        src={dish?.dishDto?.url || DISH_IMAGE_PLACEHOLDER_40}
                                        alt={
                                            getLocalizedValue(dish?.dishDto, "name", language) ||
                                            getTranslation("common.dish", language)
                                        }
                                        className="w-12 h-12 rounded-lg border dark:border-[#30363d] object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = DISH_IMAGE_PLACEHOLDER_40;
                                        }}
                                    />
                                    <div className="ml-3 flex-grow">
                                        <p className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                            {getLocalizedValue(dish?.dishDto, "name", language)}
                                        </p>
                                        <div className="flex justify-between items-center">
                                            <span className="font-bold text-m text-gray-700 dark:text-[#b1bac4]">
                                                {" "}
                                                {dish?.quantity} հատ
                                            </span>
                                            {dish?.price ? (
                                                <span className="text-gray-700 dark:text-[#b1bac4] font-medium">
                                                    {formatPriceWithCurrency(dish?.price)}
                                                </span>
                                            ) : (
                                                <span/>
                                            )}
                                        </div>
                                        {dish?.selectedSpiceLevel && (
                                            <div className="text-sm text-gray-600 dark:text-[#8b949e] mt-1">
                                                <span className="flex justify-between w-full">
                                                    <span className="font-medium">
                                                        {getTranslation("common.spiceLevel", language) || "Spice Level"}
                                                    </span>{" "}
                                                    {spiceLevels.find((s) => s.id === dish.selectedSpiceLevel)?.spiceLevel}
                                                </span>
                                            </div>
                                        )}
                                        {dish?.orderDishAdditionDtoList?.length > 0 &&
                                            dish.orderDishAdditionDtoList.map((item, idx) => (
                                                <div key={idx} className="text-sm text-gray-600 dark:text-[#8b949e]">
                                                    <span className="flex justify-between w-full">
                                                        <span className="font-medium">
                                                            {getLocalizedValue(item.dishAdditionDto.group, "name", language)}
                                                        </span>
                                                        {item.price ? (
                                                            <span className="flex justify-between w-full">
                                                                <span className="ml-2 flex">
                                                                    {getLocalizedValue(item.dishAdditionDto, "name", language)}
                                                                </span>
                                                                <span
                                                                    className="text-m text-gray-700 dark:text-[#b1bac4] font-medium">
                                                                    {formatPriceWithCurrency(item.price)}
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            <span>{getLocalizedValue(item.dishAdditionDto, "name", language)}</span>
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {
                        note && (
                            <div
                                className={`${isTakeaway
                                    ? 'bg-white dark:bg-gray-800/40 border-gray-300 dark:border-gray-700 shadow-sm'
                                    : 'bg-white dark:bg-gray-900/30 border-gray-200 dark:border-gray-700'} border-2 rounded-lg p-5 transition-all`}
                                role="status"
                                aria-live="polite"
                            >
                                <div className="flex items-center">
                                    <div className="flex-1">
                                        <span
                                            className={`text-xs font-medium uppercase tracking-wide ${isTakeaway ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`}>
                                            {getTranslation('orderContentPage.notes', language)}
                                    </span>
                                        <div className="flex items-center mt-1">
                                            <span
                                                className={`text-base font-bold text-gray-900 dark:text-gray-300`}>{note}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                    <div className="text-sm space-y-4 border-t dark:border-[#30363d] pt-4">
                        {(promoCode || promoCodeUsages?.promoCode || promoCodeUsages?.code) && (
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                    {getTranslation("orderContentPage.promocode", language)}
                                </span>
                                <span className="text-gray-600 dark:text-[#b1bac4] font-medium">
                                    {promoCode || promoCodeUsages?.promoCode || promoCodeUsages?.code}
                                </span>
                            </div>
                        )}
                        {discountAmount !== null && (
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                    {getTranslation("orderContentPage.discountAmount", language)}
                                </span>
                                <span className="text-gray-600 dark:text-[#b1bac4] font-medium">
                                    -{formatPriceWithCurrency(discountAmount)}
                                </span>
                            </div>
                        )}
                        <div
                            className="flex justify-between font-semibold text-gray-900 dark:text-[#c9d1d9] mt-4 text-lg">
                            <span>{getTranslation("orderContentPage.total", language)}:</span>
                            <span>{formattedTotalPrice}</span>
                        </div>
                        {discountAmount !== null && (
                            <div
                                className="flex justify-between font-semibold text-gray-900 dark:text-[#c9d1d9] mt-4 text-lg">
                                <span>{getTranslation("orderContentPage.totalWithDiscount", language)}:</span>
                                <span>{formattedTotalWithDiscount}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="lg:col-span-5">
                    <div
                        className="bg-white dark:bg-[#0d1117] p-6 rounded-lg shadow-sm border border-gray-100 dark:border-[#30363d]">
                        <h3 className="text-gray-900 dark:text-[#c9d1d9] text-lg font-bold mb-6 border-b border-gray-200 dark:border-[#30363d] pb-3">
                            {getTranslation("orderContentPage.additionalInfo", language)}
                        </h3>
                        <div className="space-y-6 text-sm">
                            <div
                                className={`${
                                    isTakeaway
                                        ? "bg-white dark:bg-gray-800/40 border-gray-300 dark:border-gray-700 shadow-sm"
                                        : "bg-white dark:bg-gray-900/30 border-gray-200 dark:border-gray-700"
                                } border-2 rounded-lg p-5 transition-all`}
                                role="status"
                                aria-live="polite"
                            >
                                <div className="flex items-center">
                                    {isTakeaway ? (
                                        <div
                                            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 mr-3">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="currentColor"
                                                className="w-5 h-5 text-gray-700 dark:text-gray-300"
                                            >
                                                <path
                                                    d="M8 7a4 4 0 1 1 8 0v1h1.5A1.5 1.5 0 0 1 19 9.5v9A2.5 2.5 0 0 1 16.5 21h-9A2.5 2.5 0 0 1 5 18.5v-9A1.5 1.5 0 0 1 6.5 8H8V7Zm2 1h4V7a2 2 0 1 0-4 0v1Z"/>
                                            </svg>
                                        </div>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            className="w-5 h-5 mr-2 text-gray-700 dark:text-gray-400"
                                            strokeWidth={2.5}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M3 7h12v7H3zM15 10h3l3 3v1h-6V10zM6 20a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm10 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z"
                                            />
                                        </svg>
                                    )}
                                    <div className="flex-1">
                                        <span
                                            className={`text-xs font-medium uppercase tracking-wide ${
                                                isTakeaway ? "text-gray-600 dark:text-gray-400" : "text-gray-500 dark:text-gray-400"
                                            }`}
                                        >
                                            {getTranslation("orderContentPage.deliveryWay", language)}
                                        </span>
                                        <div className="flex items-center mt-1">
                                            <span
                                                className={`text-base font-bold ${
                                                    isTakeaway ? "text-gray-800 dark:text-gray-200" : "text-gray-900 dark:text-gray-300"
                                                }`}
                                            >
                                                {isTakeaway
                                                    ? getTranslation("orderContentPage.takeaway", language)
                                                    : getTranslation("orderContentPage.delivery", language)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                className="p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-customOrange/60 dark:border-orange-500/40">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-[#8b949e] mb-2">
                                    {getTranslation("orderContentPage.customer", language)}
                                </p>
                                <div className="min-w-0 flex flex-col gap-1.5">
                                    {customerFullName ? (
                                        <>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9] leading-snug break-words">
                                                {customerFullName}
                                            </p>
                                            <div
                                                className="flex flex-col gap-1 sm:flex-row sm:items-center sm:flex-wrap sm:gap-x-2">
                                                <span
                                                    className="text-[11px] font-medium text-gray-500 dark:text-[#8b949e] shrink-0">
                                                    {getTranslation("orderContentPage.phoneNumber", language)}
                                                </span>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    {customerPhone ? (
                                                        customer.isExternalSnapshot ? (
                                                            <span className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                                {customerPhone}
                                                            </span>
                                                        ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                navigate("/clients", {state: {searchPhone: customerPhone}})
                                                            }
                                                            className="text-left text-sm font-medium text-customOrange dark:text-customOrange hover:underline transition-colors truncate max-w-full"
                                                        >
                                                            {customerPhone}
                                                        </button>
                                                        )
                                                    ) : (
                                                        <span
                                                            className="text-sm text-gray-500 dark:text-[#8b949e]">—</span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {customerPhone ? (
                                                customer.isExternalSnapshot ? (
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                                        {customerPhone}
                                                    </span>
                                                ) : (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate("/clients", {state: {searchPhone: customerPhone}})
                                                    }
                                                    className="text-left text-sm font-semibold text-customOrange dark:text-customOrange hover:underline transition-colors truncate max-w-full"
                                                >
                                                    {customerPhone}
                                                </button>
                                                )
                                            ) : (
                                                <span className="text-gray-500 dark:text-[#8b949e] text-sm">—</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {displayCompanyName && (
                                <div
                                    className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d] gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation("orderPage.company", language) || "Company"}
                                    </span>
                                    <span
                                        className="text-gray-700 dark:text-[#b1bac4] font-medium text-left sm:text-right break-words">
                                        {displayCompanyName}
                                    </span>
                                </div>
                            )}

                            <div
                                className="flex flex-col p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d] gap-3">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation("orderContentPage.scheduledDate", language)}
                                    </span>
                                    <div
                                        className="text-gray-700 dark:text-[#b1bac4] font-medium text-left sm:text-right">
                                        {formatDate(deliveryDateTime)}
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                                    <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                        {getTranslation("orderContentPage.scheduledTime", language)}
                                    </span>
                                    <div
                                        className="flex flex-wrap items-center gap-2 sm:justify-end text-left sm:text-right">
                                        <span className="text-gray-700 dark:text-[#b1bac4] font-medium">
                                            {formatTime(deliveryDateTime)}
                                        </span>
                                        {asSoonAsPossible && (
                                            <span
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-white dark:bg-gray-800/50 text-gray-800 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                                {getTranslation("orderContentPage.asSoonAsPossible", language)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {!isTakeaway && (
                                <div
                                    className="p-4 bg-white dark:bg-[#161b22] rounded-md shadow-sm border-l-4 border-gray-300 dark:border-[#30363d]">
                                    <div className="mb-3">
                                        <span className="font-semibold text-gray-900 dark:text-[#c9d1d9]">
                                            {getTranslation("orderContentPage.address", language)}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                        {addressDto?.country && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.country", language)}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                    {addressDto.country}
                                                </span>
                                            </div>
                                        )}
                                        {addressDto?.city && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.city", language)}
                                                </span>
                                                <span
                                                    className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.city}</span>
                                            </div>
                                        )}
                                        {addressDto?.street && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.street", language)}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                    {addressDto.street}
                                                </span>
                                            </div>
                                        )}
                                        {addressDto?.home && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.home", language)}
                                                </span>
                                                <span
                                                    className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.home}</span>
                                            </div>
                                        )}
                                        {addressDto?.floor && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.floor", language)}
                                                </span>
                                                <span
                                                    className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.floor}</span>
                                            </div>
                                        )}
                                        {addressDto?.entrance && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.entrance", language)}
                                                </span>
                                                <span className="font-medium text-gray-900 dark:text-[#c9d1d9]">
                                                    {addressDto.entrance}
                                                </span>
                                            </div>
                                        )}
                                        {addressDto?.flat && (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-500 dark:text-[#8b949e] font-medium">
                                                    {getTranslation("orderContentPage.flat", language)}
                                                </span>
                                                <span
                                                    className="font-medium text-gray-900 dark:text-[#c9d1d9]">{addressDto.flat}</span>
                                            </div>
                                        )}
                                    </div>
                                    <a
                                        href={yandexMapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 transition-colors mt-2 font-medium"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2.5}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                                            />
                                        </svg>
                                        {getTranslation("orderContentPage.viewOnMap", language)}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Toast ref={toastRef}/>
        </div>
    );
};

export default OrderDetailsExternal;
